import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  Pencil,
  TrendingUp,
  CheckCircle,
  XCircle,
  FileText,
  Send,
  Eye,
  IndianRupee,
  Mail,
  AlertCircle,
  AlertTriangle,
  MessageSquare,
  Calendar,
  Clock,
  Loader2,
  FileX,
  Ban,
  ArrowRight,
} from 'lucide-react';
import { Activity, ActivityType } from '@/types/activity';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

/**
 * UnifiedLeadTimeline
 *
 * Merges Activity feed entries (LEAD_*, STAGE_*, INVOICE_*, EMAIL_*, PAYMENT_*)
 * into one chronological timeline for a single lead.
 *
 * Phase 1 uses the existing GET /api/activities/recent?leadId=xxx endpoint which
 * already contains the INVOICE_* and EMAIL_* events dual-logged by W4-D1's
 * activityService helpers. Phase 2 may add a dedicated merged endpoint that
 * also folds in LeadActivity/InvoiceActivity rows directly.
 */

interface UnifiedLeadTimelineProps {
  leadId: string;
  /** Initial number of items to fetch. Defaults to 50. */
  pageSize?: number;
}

interface ActivitiesApiResponse {
  success: boolean;
  data: {
    activities: Activity[];
    hasMore: boolean;
    nextSinceId: string | null;
  };
  meta?: {
    total: number;
  };
}

type IconConfig = {
  Icon: React.ElementType;
  iconClass: string;
  bgClass: string;
  ringClass: string;
  label: string;
};

const DEFAULT_CONFIG: IconConfig = {
  Icon: Clock,
  iconClass: 'text-gray-600',
  bgClass: 'bg-gray-100',
  ringClass: 'ring-gray-200',
  label: 'Activity',
};

const ICON_MAP: Partial<Record<ActivityType, IconConfig>> = {
  // Lead lifecycle
  LEAD_CREATED: {
    Icon: UserPlus,
    iconClass: 'text-blue-600',
    bgClass: 'bg-blue-50',
    ringClass: 'ring-blue-200',
    label: 'Lead created',
  },
  LEAD_UPDATED: {
    Icon: Pencil,
    iconClass: 'text-gray-600',
    bgClass: 'bg-gray-50',
    ringClass: 'ring-gray-200',
    label: 'Lead updated',
  },
  STAGE_CHANGE: {
    Icon: ArrowRight,
    iconClass: 'text-purple-600',
    bgClass: 'bg-purple-50',
    ringClass: 'ring-purple-200',
    label: 'Stage changed',
  },
  LEAD_STAGE_CHANGED: {
    Icon: ArrowRight,
    iconClass: 'text-purple-600',
    bgClass: 'bg-purple-50',
    ringClass: 'ring-purple-200',
    label: 'Stage changed',
  },
  LEAD_CLOSED_WON: {
    Icon: CheckCircle,
    iconClass: 'text-green-600',
    bgClass: 'bg-green-50',
    ringClass: 'ring-green-200',
    label: 'Closed — Won',
  },
  LEAD_CLOSED_LOST: {
    Icon: XCircle,
    iconClass: 'text-red-600',
    bgClass: 'bg-red-50',
    ringClass: 'ring-red-200',
    label: 'Closed — Lost',
  },
  LEAD_REASSIGNED: {
    Icon: UserPlus,
    iconClass: 'text-blue-600',
    bgClass: 'bg-blue-50',
    ringClass: 'ring-blue-200',
    label: 'Reassigned',
  },
  LEAD_ASSIGNED: {
    Icon: UserPlus,
    iconClass: 'text-blue-600',
    bgClass: 'bg-blue-50',
    ringClass: 'ring-blue-200',
    label: 'Assigned',
  },
  // Tasks & notes
  TASK_CREATED: {
    Icon: Calendar,
    iconClass: 'text-orange-600',
    bgClass: 'bg-orange-50',
    ringClass: 'ring-orange-200',
    label: 'Task created',
  },
  TASK_COMPLETED: {
    Icon: CheckCircle,
    iconClass: 'text-green-600',
    bgClass: 'bg-green-50',
    ringClass: 'ring-green-200',
    label: 'Task completed',
  },
  NOTE_ADDED: {
    Icon: FileText,
    iconClass: 'text-gray-600',
    bgClass: 'bg-gray-50',
    ringClass: 'ring-gray-200',
    label: 'Note added',
  },
  // Communication
  WHATSAPP_SENT: {
    Icon: MessageSquare,
    iconClass: 'text-green-600',
    bgClass: 'bg-green-50',
    ringClass: 'ring-green-200',
    label: 'WhatsApp sent',
  },
  WHATSAPP_RECEIVED: {
    Icon: MessageSquare,
    iconClass: 'text-blue-600',
    bgClass: 'bg-blue-50',
    ringClass: 'ring-blue-200',
    label: 'WhatsApp received',
  },
  // Invoice lifecycle (W4-D1)
  INVOICE_CREATED: {
    Icon: FileText,
    iconClass: 'text-blue-600',
    bgClass: 'bg-blue-50',
    ringClass: 'ring-blue-200',
    label: 'Invoice created',
  },
  INVOICE_SENT: {
    Icon: Send,
    iconClass: 'text-blue-600',
    bgClass: 'bg-blue-50',
    ringClass: 'ring-blue-200',
    label: 'Invoice sent',
  },
  INVOICE_VIEWED: {
    Icon: Eye,
    iconClass: 'text-indigo-600',
    bgClass: 'bg-indigo-50',
    ringClass: 'ring-indigo-200',
    label: 'Invoice viewed',
  },
  INVOICE_PAID: {
    Icon: CheckCircle,
    iconClass: 'text-green-600',
    bgClass: 'bg-green-50',
    ringClass: 'ring-green-200',
    label: 'Invoice paid',
  },
  INVOICE_CANCELLED: {
    Icon: Ban,
    iconClass: 'text-red-600',
    bgClass: 'bg-red-50',
    ringClass: 'ring-red-200',
    label: 'Invoice cancelled',
  },
  INVOICE_OVERDUE: {
    Icon: AlertTriangle,
    iconClass: 'text-amber-600',
    bgClass: 'bg-amber-50',
    ringClass: 'ring-amber-200',
    label: 'Invoice overdue',
  },
  // Payment
  PAYMENT_RECORDED: {
    Icon: IndianRupee,
    iconClass: 'text-green-600',
    bgClass: 'bg-green-50',
    ringClass: 'ring-green-200',
    label: 'Payment recorded',
  },
  // Email
  EMAIL_SENT: {
    Icon: Mail,
    iconClass: 'text-gray-600',
    bgClass: 'bg-gray-50',
    ringClass: 'ring-gray-200',
    label: 'Email sent',
  },
  EMAIL_BOUNCED: {
    Icon: AlertCircle,
    iconClass: 'text-red-600',
    bgClass: 'bg-red-50',
    ringClass: 'ring-red-200',
    label: 'Email bounced',
  },
};

function getConfigForType(type: ActivityType | string): IconConfig {
  return ICON_MAP[type as ActivityType] || DEFAULT_CONFIG;
}

function formatRelativeTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  if (Number.isNaN(diffMs)) return '';
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? 's' : ''} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  return d.toLocaleDateString();
}

function fullTimestamp(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const API_BASE_URL = (() => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (
    typeof window !== 'undefined' &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1'
  ) {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:5000/api';
})();

async function fetchLeadTimeline(leadId: string, limit: number): Promise<Activity[]> {
  const token = localStorage.getItem('authToken');
  const response = await fetch(
    `${API_BASE_URL}/activities/recent?leadId=${encodeURIComponent(leadId)}&limit=${limit}`,
    {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Failed to load timeline (${response.status}): ${text || response.statusText}`);
  }

  const json: ActivitiesApiResponse = await response.json();
  if (!json.success) {
    throw new Error('Timeline request unsuccessful');
  }
  return json.data?.activities || [];
}

export const UnifiedLeadTimeline: React.FC<UnifiedLeadTimelineProps> = ({
  leadId,
  pageSize = 50,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [limit, setLimit] = useState<number>(pageSize);

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery<Activity[]>({
    queryKey: ['lead-timeline', leadId, limit],
    queryFn: () => fetchLeadTimeline(leadId, limit),
    enabled: !!leadId && !!user,
    staleTime: 30_000,
  });

  const activities = useMemo(() => data || [], [data]);
  const canShowMore = activities.length >= limit; // heuristic — assume more if we hit the limit

  const handleItemClick = (activity: Activity) => {
    const invoiceId = activity.metadata?.invoiceId;
    if (invoiceId) {
      navigate(`/invoices/${invoiceId}`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Loading activity timeline">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-start gap-3 animate-pulse">
            <div className="h-10 w-10 rounded-full bg-gray-200 flex-shrink-0" />
            <div className="flex-1 space-y-2 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-200 rounded w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="text-center py-10 border border-red-100 rounded-lg bg-red-50">
        <AlertCircle className="h-10 w-10 mx-auto mb-2 text-red-500" />
        <p className="text-sm font-medium text-red-700">Could not load timeline</p>
        <p className="text-xs text-red-600 mt-1">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-200 rounded hover:bg-red-50"
        >
          Retry
        </button>
      </div>
    );
  }

  // Empty state
  if (activities.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 border border-dashed border-gray-200 rounded-lg">
        <FileX className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p className="text-sm font-medium text-gray-700">No activity yet</p>
        <p className="text-xs text-gray-500 mt-1">
          Actions like calls, notes, invoices and emails will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical timeline rail */}
      <div
        className="absolute left-5 top-5 bottom-5 w-px bg-gray-200"
        aria-hidden="true"
      />

      <ul className="space-y-4 relative">
        {activities.map((activity) => {
          const config = getConfigForType(activity.type);
          const { Icon } = config;
          const invoiceId = activity.metadata?.invoiceId;
          const isClickable = !!invoiceId;
          const userName = activity.user?.name || activity.metadata?.userName || 'System';

          return (
            <li key={activity.id} className="relative flex items-start gap-3 pl-0">
              {/* Icon bubble */}
              <div
                className={cn(
                  'relative z-10 flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ring-4 ring-white',
                  config.bgClass
                )}
              >
                <Icon className={cn('h-5 w-5', config.iconClass)} aria-hidden="true" />
              </div>

              {/* Card body */}
              <div
                className={cn(
                  'flex-1 min-w-0 rounded-lg border border-gray-100 bg-white p-3 shadow-sm',
                  isClickable &&
                    'cursor-pointer hover:border-blue-200 hover:shadow transition'
                )}
                onClick={isClickable ? () => handleItemClick(activity) : undefined}
                onKeyDown={
                  isClickable
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleItemClick(activity);
                        }
                      }
                    : undefined
                }
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                aria-label={
                  isClickable
                    ? `${config.label}: ${activity.description}. Open invoice.`
                    : undefined
                }
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      <span
                        className={cn(
                          'inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full',
                          config.bgClass,
                          config.iconClass
                        )}
                      >
                        {config.label}
                      </span>
                      {activity.metadata?.invoiceNumber && (
                        <span className="text-xs font-mono text-gray-500">
                          {activity.metadata.invoiceNumber}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-900 break-words">
                      {activity.description}
                    </p>
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1.5 text-xs text-gray-500">
                      {userName && userName !== 'System' && <span>by {userName}</span>}
                      <span title={fullTimestamp(activity.createdAt)}>
                        {activity.relativeTime || formatRelativeTime(activity.createdAt)}
                      </span>
                    </div>
                  </div>
                  {isClickable && (
                    <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Show more */}
      {canShowMore && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setLimit((l) => l + pageSize)}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-60"
          >
            {isFetching ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </>
            ) : (
              <>
                Show more
                <TrendingUp className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default UnifiedLeadTimeline;
